import { AfterViewInit, Component, computed, signal, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { PhoneReport } from '../../../shared/models/phone-report.model';
import { Role } from '../../../shared/models/user.model';
import { BatchTasks } from '../../../shared/services/batch-tasks';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhoneReportService } from '../../../shared/services/phone-report.service';
import { UsersService } from '../../../shared/services/users.service';
import { PhoneReportsTableComponent } from '../phone-reports-table/phone-reports-table.component';

@Component({
  standalone: false,
  selector: 'app-phone-reports-index',
  templateUrl: './phone-reports-index.component.html',
  styleUrls: ['./phone-reports-index.component.scss'],
})
export class PhoneReportsIndexComponent implements AfterViewInit {
  selectedRows: PhoneReport[] = [];
  batchCompleted = 0;
  progressShow = false;

  readonly isAdmin = signal<boolean>(false);

  readonly tooltipText = computed(() => (!this.isAdmin() ? 'You cannot delete reports' : 'Delete reports'));

  searchFormControl: FormControl = new FormControl();
  searchSubscription: Subscription;
  initialSearchFromQueryParams: string | null = null;

  @ViewChild(PhoneReportsTableComponent, { static: false }) table: PhoneReportsTableComponent;

  constructor(
    public reports: PhoneReportService,
    public router: Router,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    public userService: UsersService,
    titleService: Title
  ) {
    titleService.setTitle('Phone reports');
    this.searchSubscription = this.searchFormControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(_ => {
      this.onChangeInputSearch(_);
    });
    this.initialSearchFromQueryParams = this.route.snapshot.queryParamMap.get('search');
    if (this.initialSearchFromQueryParams) {
      this.searchFormControl.setValue(this.initialSearchFromQueryParams, { emitEvent: false });
    }
    this.isAdmin.set(this.userService.authUser?.role === Role.ADMIN);
  }

  ngAfterViewInit() {
    if (!this.initialSearchFromQueryParams) {
      return;
    }

    this.onChangeInputSearch(this.initialSearchFromQueryParams);
  }

  batchDelete() {
    const tasks = this.selectedRows.map(row => {
      return this.reports.delete(row.id).pipe(
        map(() => {
          this.notificationService.success('Reports deleted', 'Phone reports');
          this.completeTask();
        }),
        catchError((error, caught) => {
          this.completeTask();
          this.notificationService.error({
            title: 'Phone reports',
            message: 'Report delete an error',
            serviceName: 'API GATEWAY',
            requestMessage: error.statusText,
            requestCode: error.status,
          });
          return caught;
        })
      );
    });
    this.startTasks(tasks);
  }

  onChangeInputSearch(phrase) {
    if (phrase instanceof Event) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { search: phrase },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.table.requestParams.search = phrase;
    this.table.requestParams.page = 1;
    this.table.update();
  }

  startTasks(tasks: Observable<any>[]) {
    this.progressShow = true;
    this.batchCompleted = 0;
    this.table.loading = true;
    new BatchTasks(tasks).run(4);
  }

  completeTask() {
    this.batchCompleted++;
    if (this.selectedRows.length === this.batchCompleted) {
      this.table.loading = false;
      this.progressShow = false;
      this.selectedRows = [];
      this.table.update();
    }
  }

  batchClear() {
    this.progressShow = false;
    this.selectedRows = [];
    this.table.resetBatch();
  }
}
